import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/shadcn';
import type { DashboardRollupFreshness } from '../../../types';

interface RollupLagNoticeProps {
  rollup?: DashboardRollupFreshness;
}

/** "6h 17m", "12m", "45s" — enough precision to judge, short enough to inline. */
export function formatLag(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return 'unknown';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours < 24) return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/**
 * Shown when the hourly rollup worker has fallen behind.
 *
 * The numbers stay correct either way — the backend widens its live query to
 * cover whatever the rollup is missing — so this is an operations warning, not
 * a data warning. Saying so explicitly matters: the alternative reading, that
 * the figures on screen are wrong, would send an admin chasing the wrong thing.
 */
const RollupLagNotice: React.FC<RollupLagNoticeProps> = ({ rollup }) => {
  if (!rollup?.stale) return null;

  const lag = rollup.watermark_at === null ? null : formatLag(rollup.lag_seconds);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Rollup worker is behind"
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          {lag === null ? 'Rollup not running' : `Rollup ${lag} behind`}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        The hourly rollup worker{' '}
        {lag === null ? 'has never run in this environment' : `last completed ${lag} ago`}. These
        numbers are still complete — the missing hours are being read straight from the ledger
        instead — but queries are slower until the worker catches up.
      </TooltipContent>
    </Tooltip>
  );
};

export default RollupLagNotice;
